<template>
  <div
    class="atelier min-h-screen bg-[#f6f3ee] font-sans text-[#141414] antialiased"
  >
    <div
      v-if="status !== 'authorized'"
      class="flex min-h-screen items-center justify-center"
    >
      <p class="text-[11px] uppercase tracking-[0.32em] text-[#8a847a]">
        {{ status === "checking" ? "Vérification de l’accès" : "Accès refusé" }}
      </p>
    </div>

    <template v-else>
      <header
        ref="atelierHeader"
        class="sticky top-0 z-30 overflow-y-clip border-b bg-[#f6f3ee]/95 backdrop-blur"
        :class="
          stockMode === 'simulation' ? 'border-[#d6336c]' : 'border-[#e4ded4]'
        "
      >
        <div
          v-if="stockMode === 'simulation'"
          ref="simulationBanner"
          class="flex items-center justify-center gap-3 bg-[#d6336c] px-4 py-1 text-[10px] uppercase tracking-[0.2em] text-white"
          role="status"
        >
          <span class="truncate"
            >Mode simulation : les stocks affichés sont simulés, le stock réel
            n’est pas modifié</span
          >
          <button
            type="button"
            class="shrink-0 whitespace-nowrap underline underline-offset-4"
            @click="stockMode = 'real'"
          >
            Quitter
          </button>
        </div>
        <div
          class="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 sm:px-8 md:pl-[5.5rem]"
        >
          <div class="flex min-h-8 items-center justify-between gap-4">
            <NuxtLink
              to="/app"
              class="flex shrink-0 items-baseline gap-3 whitespace-nowrap"
            >
              <span class="font-serif text-xl tracking-[0.18em]">KENDRICK</span>
              <span
                class="hidden text-[9px] uppercase tracking-[0.28em] text-[#8a847a] sm:inline"
                >Atelier</span
              >
            </NuxtLink>

            <AtelierStockModeSwitch v-model="stockMode" />

            <div class="hidden shrink-0 items-center justify-end gap-4 sm:flex">
              <a
                href="https://kendrick.paris/"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#e4ded4] text-[#5c574f] transition hover:border-[#141414] hover:text-[#141414]"
                aria-label="Ouvrir le site Kendrick dans une nouvelle fenêtre"
                title="Ouvrir le site"
              >
                <ExternalLink class="h-4 w-4" aria-hidden="true" />
              </a>
              <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#e4ded4] text-[#5c574f] transition hover:border-[#141414] hover:text-[#141414]"
                :aria-pressed="linkSoundEnabled"
                :aria-label="
                  linkSoundEnabled
                    ? 'Désactiver le son des clics sur les liens'
                    : 'Activer le son des clics sur les liens'
                "
                :title="
                  linkSoundEnabled
                    ? 'Son des clics activé'
                    : 'Son des clics désactivé'
                "
                @click="toggleLinkSound"
              >
                <Bell v-if="linkSoundEnabled" class="h-4 w-4" />
                <BellOff v-else class="h-4 w-4" />
              </button>
              <span
                class="inline-flex items-center gap-1.5 whitespace-nowrap text-[10px] uppercase tracking-[0.2em]"
                :class="
                  linkConnection === 'live'
                    ? 'text-[#3f7d4e]'
                    : linkConnection === 'error'
                      ? 'text-[#8c3b30]'
                      : 'text-[#a39d93]'
                "
                :title="
                  linkConnection === 'live'
                    ? 'Alertes de clics en direct'
                    : linkConnection === 'error'
                      ? 'Alertes de clics déconnectées, nouvelle tentative…'
                      : 'Connexion aux alertes de clics…'
                "
              >
                <span
                  class="h-1.5 w-1.5 rounded-full"
                  :class="
                    linkConnection === 'live'
                      ? 'bg-[#3f7d4e]'
                      : linkConnection === 'error'
                        ? 'bg-[#8c3b30]'
                        : 'bg-[#d9d2c5]'
                  "
                />
                {{
                  linkConnection === "live"
                    ? "Live"
                    : linkConnection === "error"
                      ? "Hors ligne"
                      : "Connexion…"
                }}
              </span>
              <span
                class="max-w-[140px] truncate text-xs text-[#8a847a] xl:max-w-[180px]"
                >{{ email }}</span
              >
              <button
                type="button"
                class="whitespace-nowrap text-[10px] uppercase tracking-[0.2em] text-[#5c574f] hover:text-[#141414]"
                @click="signOut"
              >
                Déconnexion
              </button>
            </div>
          </div>

          <nav
            class="flex min-w-0 items-center gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden text-[9px] uppercase tracking-[0.18em] md:hidden xl:text-[10px]"
            aria-label="Navigation principale"
          >
            <NuxtLink
              v-for="item in nav"
              :key="item.to"
              :to="normalizeAtelierPath(item.to)"
              class="inline-flex min-h-8 shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1.5 text-[#5c574f] transition hover:text-[#141414]"
              :class="{ 'bg-[#141414] !text-[#f6f3ee]': isActive(item) }"
            >
              {{ item.label }}
            </NuxtLink>
          </nav>

          <section
            v-if="pinnedLinks.length"
            class="flex h-14 min-h-0 min-w-0 items-center gap-3 overflow-hidden border-t border-[#e4ded4] pt-1"
            aria-label="Liens suivis"
          >
            <div
              class="flex min-h-0 min-w-0 items-center gap-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div
                v-for="link in pinnedLinks"
                :key="link.id"
                class="flex h-11 min-w-0 max-w-full shrink-0 items-center gap-2 rounded-xl border border-[#e4ded4] bg-white px-2.5 py-0.5"
              >
                <AtelierPinnedLinkMonitor
                  :label="link.label"
                  :click-count="link.click_count"
                  :history="link.history"
                />
              </div>
            </div>
          </section>
        </div>
      </header>

      <aside
        class="fixed bottom-0 left-0 z-40 hidden border-r border-[#e4ded4] bg-[#f6f3ee] md:flex md:flex-col"
        :class="sidebarCollapsed ? 'w-16' : 'w-60'"
        :style="{ top: `${simulationBannerHeight}px` }"
      >
        <div
          class="flex items-center border-b border-[#e4ded4] p-2"
          :class="sidebarCollapsed ? 'justify-center' : 'justify-start'"
        >
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5c574f] transition hover:bg-white hover:text-[#141414]"
            :aria-label="
              sidebarCollapsed ? 'Déployer le menu' : 'Réduire le menu'
            "
            :title="sidebarCollapsed ? 'Déployer le menu' : 'Réduire le menu'"
            :aria-expanded="!sidebarCollapsed"
            @click="toggleSidebar"
          >
            <ChevronsRight
              v-if="sidebarCollapsed"
              class="h-4 w-4"
              aria-hidden="true"
            />
            <ChevronsLeft v-else class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <nav
          class="min-h-0 flex-1 space-y-1 overflow-y-auto p-2"
          aria-label="Navigation principale"
        >
          <template v-for="group in navGroups" :key="group.key">
            <button
              v-if="!sidebarCollapsed"
              type="button"
              class="mt-3 flex w-full items-center justify-between px-3 py-2 text-[9px] uppercase tracking-[0.2em] text-[#8a847a] hover:text-[#141414]"
              :aria-expanded="!collapsedGroups[group.key]"
              @click="toggleNavGroup(group.key)"
            >
              <span>{{ group.label }}</span
              ><span class="text-sm leading-none">{{
                collapsedGroups[group.key] ? "+" : "−"
              }}</span>
            </button>
            <div
              v-if="!collapsedGroups[group.key]"
              class="space-y-1"
              :class="sidebarCollapsed ? '' : 'border-l border-[#e4ded4] pl-1'"
            >
              <div
                v-for="item in group.items"
                :key="item.to"
                class="group/nav relative"
              >
                <NuxtLink
                  :to="normalizeAtelierPath(item.to)"
                  class="group flex min-h-10 items-center gap-3 rounded-lg px-3 text-[10px] uppercase tracking-[0.14em] text-[#5c574f] transition hover:bg-white hover:text-[#141414]"
                  :class="[
                    isActive(item)
                      ? '!bg-[#141414] !text-[#f6f3ee] hover:!bg-[#141414]'
                      : '',
                    sidebarCollapsed ? 'justify-center px-0' : 'pr-9',
                  ]"
                  :title="sidebarCollapsed ? item.label : undefined"
                  @mouseenter="
                    normalizeAtelierPath(item.to) === '/app/liens'
                      ? openLinksFlyout($event)
                      : undefined
                  "
                  @mouseleave="
                    normalizeAtelierPath(item.to) === '/app/liens'
                      ? scheduleLinksFlyoutClose()
                      : undefined
                  "
                  @focus="
                    normalizeAtelierPath(item.to) === '/app/liens'
                      ? openLinksFlyout($event)
                      : undefined
                  "
                >
                  <component
                    :is="item.icon"
                    class="h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  <span v-if="!sidebarCollapsed" class="truncate">{{
                    item.label
                  }}</span>
                </NuxtLink>
                <a
                  v-if="!sidebarCollapsed"
                  :href="normalizeAtelierPath(item.to)"
                  target="_blank"
                  rel="noopener"
                  class="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-[#8a847a] opacity-0 transition hover:bg-[#e4ded4] hover:text-[#141414] focus-visible:opacity-100 group-hover/nav:opacity-100"
                  :class="
                    isActive(item)
                      ? '!text-[#f6f3ee]/70 hover:!bg-white/10 hover:!text-[#f6f3ee]'
                      : ''
                  "
                  :aria-label="`Ouvrir ${item.label} dans un nouvel onglet`"
                >
                  <ExternalLink class="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </div>
            </div>
          </template>
        </nav>
        <AtelierLinksFlyout
          :open="linksFlyout.open"
          :top="linksFlyout.top"
          :left="linksFlyout.left"
          @keep="cancelLinksFlyoutClose"
          @leave="scheduleLinksFlyoutClose"
        />
      </aside>

      <main
        class="mx-auto max-w-7xl px-4 pb-24 pt-8 sm:px-8 sm:pt-12 md:pl-[5.5rem]"
      >
        <slot />
      </main>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  Bell,
  BellOff,
  Boxes,
  Coins,
  MapPin,
  BriefcaseBusiness,
  ChartNoAxesColumn,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  CreditCard,
  ExternalLink,
  LayoutDashboard,
  Factory,
  Filter,
  Grid2X2,
  Layers3,
  Link2,
  ListTodo,
  Mail,
  PackageCheck,
  Route,
  PanelsTopLeft,
  Presentation,
  ShieldCheck,
  ShoppingBag,
  Store,
  UsersRound,
  DoorOpen,
  Compass,
} from "lucide-vue-next";

const route = useRoute();
const stockMode = useStockMode();
const { authHeaders, email, status, verify, signOut } = useAtelierSession();

// Live toast + beep on shortlink clicks and signups, on every Atelier page.
const {
  soundEnabled: linkSoundEnabled,
  toggleSound: toggleLinkSound,
  connection: linkConnection,
} = useLinkClickAlerts(computed(() => status.value === "authorized"));
useManufacturingStatusAlerts(computed(() => status.value === "authorized"));

const SIDEBAR_STORAGE_KEY = "kendrick_atelier_sidebar_collapsed";
const sidebarCollapsed = ref(false);
const atelierHeader = ref<HTMLElement | null>(null);
const simulationBanner = ref<HTMLElement | null>(null);
const headerHeight = ref(68);
const simulationBannerHeight = ref(0);
let headerResizeObserver: ResizeObserver | undefined;
let simulationBannerResizeObserver: ResizeObserver | undefined;

// Pinned links add a second header row; keep the sidebar controls below it.
watch(
  atelierHeader,
  (header) => {
    headerResizeObserver?.disconnect();
    if (!header) return;
    const updateHeaderHeight = () => {
      headerHeight.value = Math.ceil(header.getBoundingClientRect().height);
      // Shared with pages that stick their own bar right under the header.
      document.documentElement.style.setProperty(
        "--atelier-header-h",
        `${headerHeight.value}px`,
      );
    };
    updateHeaderHeight();
    headerResizeObserver = new ResizeObserver(updateHeaderHeight);
    headerResizeObserver.observe(header);
  },
  { flush: "post" },
);

watch(
  simulationBanner,
  (banner) => {
    simulationBannerResizeObserver?.disconnect();
    simulationBannerHeight.value = banner
      ? Math.ceil(banner.getBoundingClientRect().height)
      : 0;
    if (!banner) return;
    const updateSimulationBannerHeight = () => {
      simulationBannerHeight.value = Math.ceil(
        banner.getBoundingClientRect().height,
      );
    };
    simulationBannerResizeObserver = new ResizeObserver(
      updateSimulationBannerHeight,
    );
    simulationBannerResizeObserver.observe(banner);
  },
  { flush: "post" },
);

interface PinnedHeaderLink {
  id: number;
  label: string;
  click_count: number;
  history: Array<{ bucket: string; clicks: number }>;
}

const PINNED_STORAGE_KEY = "kendrick_links_pinned";
const pinnedIds = ref<number[]>([]);
const pinnedLinks = ref<PinnedHeaderLink[]>([]);
let pinnedRefreshTimer: ReturnType<typeof setInterval> | null = null;

const loadPinnedLinks = async () => {
  if (status.value !== "authorized") return;
  try {
    const stored = JSON.parse(localStorage.getItem(PINNED_STORAGE_KEY) || "[]");
    pinnedIds.value = Array.isArray(stored)
      ? stored.filter((id): id is number => Number.isInteger(id))
      : [];
    if (!pinnedIds.value.length) {
      pinnedLinks.value = [];
      return;
    }
    const [linksResponse, statsResponse] = await Promise.all([
      $fetch<{
        links: Array<{ id: number; label: string; click_count: number }>;
      }>("/api/app2/links", { headers: await authHeaders() }),
      $fetch<{
        buckets: Array<{ link_id: number; bucket: string; clicks: number }>;
      }>("/api/app2/links/stats?days=30", { headers: await authHeaders() }),
    ]);
    const historyByLink = new Map<
      number,
      Array<{ bucket: string; clicks: number }>
    >();
    for (const row of statsResponse.buckets || []) {
      const history = historyByLink.get(row.link_id) || [];
      history.push({ bucket: row.bucket, clicks: row.clicks });
      historyByLink.set(row.link_id, history);
    }
    const pinned = new Set(pinnedIds.value);
    pinnedLinks.value = (linksResponse.links || [])
      .filter((link) => pinned.has(link.id))
      .map((link) => ({ ...link, history: historyByLink.get(link.id) || [] }));
  } catch {
    // The global header stays usable when the optional tracking data is unavailable.
  }
};

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  try {
    localStorage.setItem(
      SIDEBAR_STORAGE_KEY,
      sidebarCollapsed.value ? "1" : "0",
    );
  } catch {
    // Keep the preference for the current session if storage is unavailable.
  }
};

const linksFlyout = reactive({ open: false, top: 0, left: 0 });
let linksFlyoutTimer: ReturnType<typeof setTimeout> | undefined;

const cancelLinksFlyoutClose = () => {
  if (linksFlyoutTimer) clearTimeout(linksFlyoutTimer);
};

const openLinksFlyout = (event: Event) => {
  cancelLinksFlyoutClose();
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  linksFlyout.top = Math.max(8, Math.min(rect.top, window.innerHeight - 340));
  linksFlyout.left = rect.right + 8;
  linksFlyout.open = true;
};

const scheduleLinksFlyoutClose = () => {
  cancelLinksFlyoutClose();
  linksFlyoutTimer = setTimeout(() => {
    linksFlyout.open = false;
  }, 160);
};

const nav = [
  { to: "/app/2", label: "Vue d’ensemble", exact: true, icon: LayoutDashboard },
  { to: "/app/2/leads", label: "Leads", exact: false, icon: UsersRound },
  { to: "/app/2/emails", label: "Emails", exact: false, icon: Mail },
  {
    to: "/app/2/precommande",
    label: "Précommande",
    exact: true,
    icon: ClipboardList,
  },
  { to: "/app/2/preorders", label: "Campagnes", exact: false, icon: Layers3 },
  {
    to: "/app/2/precommande-paiement",
    label: "Encaissement",
    exact: false,
    icon: CreditCard,
  },
  {
    to: "/app/2/prets",
    label: "Prêts de paires",
    exact: false,
    icon: PackageCheck,
  },
  { to: "/app/2/orders", label: "Orders", exact: false, icon: ShoppingBag },
  { to: "/app/2/suivi", label: "Suivi", exact: false, icon: Route },
  { to: "/app/2/stores", label: "Boutiques", exact: false, icon: MapPin },
  { to: "/app/2/stocks", label: "Stocks", exact: false, icon: Boxes },
  {
    to: "/app/2/fabrication",
    label: "Fabrication",
    exact: false,
    icon: Factory,
  },
  {
    to: "/app/2/fabricants",
    label: "Fabricants",
    exact: false,
    icon: Factory,
  },
  {
    to: "/app/2/valeur-stock",
    label: "Valeur du stock",
    exact: false,
    icon: Coins,
  },
  {
    to: "/app/2/business-plan",
    label: "Business plan",
    exact: false,
    icon: ChartNoAxesColumn,
  },
  { to: "/app/2/wholesales", label: "Wholesales", exact: false, icon: Store },
  { to: "/app/2/actions", label: "Actions", exact: false, icon: ListTodo },
  { to: "/app/2/discovery", label: "Découverte", exact: false, icon: Compass },
  { to: "/app/2/liens", label: "Liens", exact: false, icon: Link2 },
  { to: "/app/2/entonnoir", label: "Entonnoir", exact: false, icon: Filter },
  {
    to: "/app/2/investors",
    label: "Investors",
    exact: false,
    icon: BriefcaseBusiness,
  },
  {
    to: "/app/2/ouvertures",
    label: "Ouvertures",
    exact: false,
    icon: DoorOpen,
  },
  { to: "/app/2/personas", label: "Personas", exact: false, icon: UsersRound },
  { to: "/app/2/collection", label: "Collection", exact: false, icon: Grid2X2 },
  {
    to: "/app/2/brand-platform",
    label: "Brand platform",
    exact: false,
    icon: PanelsTopLeft,
  },
  {
    to: "/app/2/espace-investisseur",
    label: "Investisseur",
    exact: false,
    icon: Presentation,
  },
  { to: "/app/backoffice", label: "BO", exact: true, icon: ShieldCheck },
];

const navGroups = [
  {
    key: "overview",
    label: "Vue d’ensemble",
    paths: ["/app/2", "/app/2/leads", "/app/2/entonnoir", "/app/2/emails"],
  },
  {
    key: "sales",
    label: "Vente & distribution",
    paths: [
      "/app/2/precommande",
      "/app/2/preorders",
      "/app/2/precommande-paiement",
      "/app/2/orders",
      "/app/2/stores",
      "/app/2/stocks",
    ],
  },
  {
    key: "production",
    label: "Fabrication",
    paths: [
      "/app/2/fabrication",
      "/app/2/fabricants",
      "/app/2/prets",
      "/app/2/suivi",
    ],
  },
  {
    key: "brand",
    label: "Marque & développement",
    paths: [
      "/app/2/personas",
      "/app/2/collection",
      "/app/2/brand-platform",
      "/app/2/discovery",
      "/app/2/liens",
      "/app/2/ouvertures",
    ],
  },
  {
    key: "management",
    label: "Pilotage",
    paths: [
      "/app/2/valeur-stock",
      "/app/2/business-plan",
      "/app/2/wholesales",
      "/app/2/investors",
      "/app/2/espace-investisseur",
      "/app/2/actions",
      "/app/backoffice",
    ],
  },
].map((group) => ({
  ...group,
  items: group.paths
    .map((path) => nav.find((item) => item.to === path))
    .filter(Boolean),
})) as Array<{
  key: string;
  label: string;
  paths: string[];
  items: typeof nav;
}>;
const NAV_GROUPS_STORAGE_KEY = "kendrick_atelier_nav_groups_collapsed";
const collapsedGroups = reactive<Record<string, boolean>>({});
const toggleNavGroup = (key: string) => {
  collapsedGroups[key] = !collapsedGroups[key];
  try {
    localStorage.setItem(
      NAV_GROUPS_STORAGE_KEY,
      JSON.stringify(collapsedGroups),
    );
  } catch {
    /* local preference only */
  }
};

const isActive = (item: { to: string; exact: boolean }) => {
  const path = normalizeAtelierPath(item.to);
  if (path === "/app") return route.path === "/app";
  return item.exact ? route.path === path : route.path.startsWith(path);
};

const normalizeAtelierPath = (path: string) =>
  path.replace(/^\/app\/2(?=\/|$)/, "/app");

useHead({
  title: "Atelier — Kendrick",
  meta: [{ name: "robots", content: "noindex, nofollow" }],
});

onMounted(() => {
  try {
    sidebarCollapsed.value = localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    Object.assign(
      collapsedGroups,
      JSON.parse(localStorage.getItem(NAV_GROUPS_STORAGE_KEY) || "{}"),
    );
  } catch {
    // Keep the expanded sidebar when storage is unavailable.
  }
  void loadPinnedLinks();
  pinnedRefreshTimer = setInterval(() => {
    void loadPinnedLinks();
  }, 30_000);
  void verify();
});

watch(status, (value) => {
  if (value === "authorized") void loadPinnedLinks();
});

const onPinnedLinksChanged = () => {
  void loadPinnedLinks();
};

onMounted(() =>
  window.addEventListener(
    "kendrick:pinned-links-changed",
    onPinnedLinksChanged,
  ),
);
onBeforeUnmount(() => {
  headerResizeObserver?.disconnect();
  simulationBannerResizeObserver?.disconnect();
  if (pinnedRefreshTimer) clearInterval(pinnedRefreshTimer);
  window.removeEventListener(
    "kendrick:pinned-links-changed",
    onPinnedLinksChanged,
  );
});
</script>

<style>
/* Atelier scrollbars: transparent track, only the thumb shows (dark, slightly squared). Beats the global grey/dark rules in main.css and app.vue. */
html:has(.atelier)::-webkit-scrollbar,
.atelier ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  background: transparent;
}
html:has(.atelier)::-webkit-scrollbar-track,
.atelier ::-webkit-scrollbar-track {
  background: transparent;
}
html:has(.atelier)::-webkit-scrollbar-thumb,
.atelier ::-webkit-scrollbar-thumb {
  background: rgba(107, 101, 91, 0.35);
  border-radius: 2px;
  border: 0;
}
html:has(.atelier)::-webkit-scrollbar-thumb:hover,
.atelier ::-webkit-scrollbar-thumb:hover {
  background: rgba(20, 20, 20, 0.7);
}
html:has(.atelier)::-webkit-scrollbar-corner,
.atelier ::-webkit-scrollbar-corner {
  background: transparent;
}
</style>
